// src/components/Dashboard/AdminDashboard/AllUsers/AdminRoleVerification.js

import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../../../../config';
import { apiFetch } from '../../../../apiFetch';
import './AdminRoleVerification.css'; // CSS for this component

const AdminRoleVerification = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [rejectionReasons, setRejectionReasons] = useState({});
    const [resubmissionAllowed, setResubmissionAllowed] = useState({});
    const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'agent' | 'scout' | 'club'
    const [searchTerm, setSearchTerm] = useState('');   // 🔍 search text

    // 🔄 Fetch all role verification requests (Agent + Scout + Club)
    useEffect(() => {
        const fetchRequests = async () => {
            try {
                setError('');
                const res = await apiFetch('/api/admin/role-verifications');
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();

                setRequests(data || []);

                // Initialize checkbox states from backend data
                const allowedMap = {};
                (data || []).forEach((req) => {
                    allowedMap[req.id] = req.allowResubmission || false;
                });
                setResubmissionAllowed(allowedMap);
            } catch (err) {
                console.error('Error fetching verification requests:', err);
                setError(err.message || 'Failed to load verification requests.');
            } finally {

                setLoading(false);
            }
        };

        fetchRequests();
    }, []);


    const getFileUrl = (rawUrl) => {
        if (!rawUrl) return null;
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
        return `${BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    };

    const getFrontendLink = (raw) => {
        if (!raw) return null;
        if (raw.includes('localhost')) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
    };




    // ✅ Approve / Reject handler
    const handleUpdate = async (id, approved, rejectionReason = '') => {
        const allowResubmission = !!resubmissionAllowed[id];

        try {
            const res = await apiFetch(`/api/admin/role-verifications/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reviewed: true,
                    approved,
                    rejectionReason,
                    allowResubmission,
                }),
            });

            if (!res.ok) throw new Error(await res.text());

            setRequests((prev) =>
                prev.map((req) =>
                    req.id === id
                        ? {
                            ...req,
                            reviewed: true,
                            approved,
                            rejectionReason,
                            allowResubmission,
                        }
                        : req
                )
            );

            // ✅ keep checkbox state in sync too
            setResubmissionAllowed((prev) => ({
                ...prev,
                [id]: allowResubmission,
            }));


        } catch (err) {
            console.error('Error updating status:', err);
            alert(err.message || 'Failed to update status.');
        }

    };

    // ✅ Toggle resubmission (safely sends full data so backend doesn't wipe fields)
    const handleToggleAllowResubmission = async (id) => {
        const previousValue = !!resubmissionAllowed[id];
        const newValue = !previousValue;

        // ✅ Optimistic UI update
        setResubmissionAllowed((prev) => ({ ...prev, [id]: newValue }));

        const current = requests.find((r) => r.id === id);
        if (!current) {
            console.warn('Request not found for id:', id);

            // ✅ revert UI
            setResubmissionAllowed((prev) => ({ ...prev, [id]: previousValue }));
            alert('Request not found. Please refresh.');

            return;
        }

        try {
            const res = await apiFetch(`/api/admin/role-verifications/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reviewed: current.reviewed,
                    approved: current.approved,
                    rejectionReason: current.rejectionReason,
                    allowResubmission: newValue,
                }),
            });

            if (!res.ok) throw new Error(await res.text());

            // keep requests state in sync
            setRequests((prev) =>
                prev.map((req) =>
                    req.id === id ? { ...req, allowResubmission: newValue } : req
                )
            );
        } catch (err) {
            console.error('Error toggling resubmission permission:', err);

            // ✅ revert checkbox if request fails
            setResubmissionAllowed((prev) => ({ ...prev, [id]: previousValue }));

            alert(err.message || 'Failed to update resubmission permission.');
        }
    };


    // 🔍 Role + text filter combined
    const normalizedSearch = searchTerm.toLowerCase().trim();

    const filteredRequests = requests
        // role filter
        .filter((req) =>
            roleFilter === 'all' ? true : req.role === roleFilter
        )
        // search filter (name, license, organization)
        .filter((req) => {
            if (!normalizedSearch) return true;

            const fieldsToSearch = [
                req.fullName,
                req.licenseNumber,
                req.agencyName,
            ];

            return fieldsToSearch.some((field) =>
                field
                    ? String(field).toLowerCase().includes(normalizedSearch)
                    : false
            );
        });

    return (
        <div className="review-container">
            <h2>USER VERIFICATION (AGENT, SCOUT, CLUB)</h2>

            {/* 🔍 Search bar UNDER title */}
            <div className="verification-search-wrapper">
                <input
                    type="text"
                    className="verification-search-input"
                    placeholder="Search by name, license number, or organization..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Role filter UNDER search bar */}
            <div className="role-filter-row">
                <label>
                    <strong>Filter by role:</strong>{' '}
                </label>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">All</option>
                    <option value="agent">Agents</option>
                    <option value="scout">Scouts</option>
                    <option value="club">Clubs</option>
                </select>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : error ? (
                <p className="error">{error}</p>
            ) : filteredRequests.length === 0 ? (
                <p>No verification requests found.</p>
            ) : (
                <div className="request-list">
                    {filteredRequests.map((req) => (
                        <div key={req.id} className="request-card">
                            <p>
                                <strong>Role:</strong>{' '}
                                {req.role
                                    ? req.role.charAt(0).toUpperCase() + req.role.slice(1)
                                    : '—'}
                            </p>
                            <p>
                                <strong>Name:</strong> {req.fullName || '—'}
                            </p>
                            <p>
                                <strong>License Number:</strong> {req.licenseNumber || '—'}
                            </p>
                            <p>
                                <strong>Issuing Body:</strong> {req.issuingBody || '—'}
                            </p>
                            <p>
                                <strong>Agency / Organization:</strong> {req.agencyName || '—'}
                            </p>
                            <p>
                                <strong>Website:</strong> {req.agencyWebsite || '—'}
                            </p>
                            <p>
                                <strong>License File:</strong>{' '}
                                {req.licenseFileUrl ? (
                                    <a
                                        href={getFileUrl(req.licenseFileUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </p>
                            <p>
                                <strong>ID File:</strong>{' '}
                                {req.idFileUrl ? (
                                    <a
                                        href={getFileUrl(req.idFileUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </p>
                            <p>
                                <strong>Agreed to Terms:</strong>{' '}
                                {req.agreedToTerms ? '✅ Yes' : '❌ No'}
                            </p>
                            <p>
                                <strong>Status:</strong>{' '}
                                {req.reviewed
                                    ? req.approved
                                        ? '✅ Approved'
                                        : '❌ Rejected'
                                    : '⏳ Pending'}
                            </p>
                            <p>
                                <strong>Profile URL:</strong>{' '}
                                {getFrontendLink(req.agentProfileUrl) ? (
                                    <a
                                        href={getFrontendLink(req.agentProfileUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        View Profile
                                    </a>
                                ) : (
                                    '—'
                                )}

                            </p>

                            <label style={{ display: 'block', marginTop: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={resubmissionAllowed[req.id] || false}
                                    onChange={() => handleToggleAllowResubmission(req.id)}
                                />
                                {' '}Allow Resubmission
                            </label>

                            {!req.reviewed && (
                                <div className="action-buttons">
                                    <textarea
                                        placeholder="Reason for rejection (optional)"
                                        value={rejectionReasons[req.id] || ''}
                                        onChange={(e) =>
                                            setRejectionReasons((prev) => ({
                                                ...prev,
                                                [req.id]: e.target.value,
                                            }))
                                        }
                                        className="rejection-textarea"
                                    />
                                    <button
                                        className="approve-btn"
                                        onClick={() => handleUpdate(req.id, true)}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        className="reject-btn"
                                        onClick={() =>
                                            handleUpdate(
                                                req.id,
                                                false,
                                                rejectionReasons[req.id] || ''
                                            )
                                        }
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminRoleVerification;






